/** Display formatting shared by the pages (UX spec §4.7). Energy and weight units are in units.ts. */
import { formatShortDateWithYear, LOCALE } from './days'

const MINUS = '−'

/** A whole number with thousands separators and a real minus: "8,412", "−120". */
export function formatInt(value: number): string {
  const rounded = Math.round(value)
  const text = Math.abs(rounded).toLocaleString(LOCALE)
  return rounded < 0 ? `${MINUS}${text}` : text
}

/** A percentage with no space: "21%", "0.5%". [digits] is the most decimals shown; trailing zeros go. */
export function formatPercent(value: number, digits = 0): string {
  const text = trimZeros(Math.abs(value).toFixed(digits))
  return `${value < 0 && Number(text) !== 0 ? MINUS : ''}${text}%`
}

/** "19 Oct" in [today]'s year, "19 Oct 2025" otherwise: dates in lists, tables and charts. Never ISO. */
export function formatDate(isoDate: string, today: string): string {
  return formatShortDateWithYear(isoDate, today)
}

/** "21 Sep – 20 Oct": an en dash with spaces; the year shows on any end outside [today]'s year. */
export function formatRange(from: string, to: string, today: string): string {
  return `${formatDate(from, today)} – ${formatDate(to, today)}`
}

/** Grams of a macro: whole numbers from 10 g up, one decimal below ("7.5", "12"). */
export function formatGrams(value: number): string {
  if (Math.abs(value) >= 10) return String(Math.round(value))
  return trimZeros(value.toFixed(1))
}

/**
 * Protein, fat and carbs in grams: "P 150 · F 65 · C 205" (labelled, under a food or a day's target), or "150 / 65 /
 * 205" in a table whose header names the three.
 */
export function formatMacros(macros: { protein: number; fat: number; carbs: number }, style: 'labelled' | 'slashes' = 'labelled'): string {
  const [protein, fat, carbs] = [macros.protein, macros.fat, macros.carbs].map(formatGrams)
  return style === 'slashes' ? `${protein} / ${fat} / ${carbs}` : `P ${protein} · F ${fat} · C ${carbs}`
}

/** A count and its noun, plural unless one: "1 food", "3 foods". [many] for nouns that do not just add "s". */
export function plural(count: number, noun: string, many = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : many}`
}

/** An amount without trailing zeros: 1, 1.5, 150, 0.33. */
export function formatAmount(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return trimZeros(value.toFixed(2))
}

function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text
}

/** "08:05" for minute 485. */
export function formatTime(minute: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`
}

/** Minute of the day from "HH:MM", or null when the text is not a time. */
export function parseTime(text: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** "1.5 × 1 cup · 354 g" for an amount entered in a portion, "150 g" otherwise. */
export function formatQuantity(grams: number, label: string | null, portionGrams: number | null): string {
  if (label === null || portionGrams === null || portionGrams <= 0) return `${formatAmount(round(grams, 1))} g`
  const count = round(grams / portionGrams, 2)
  return `${formatAmount(count)} × ${label} · ${Math.round(grams)} g`
}

/** Rounds to [digits] decimals. */
export function round(value: number, digits = 0): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

/** "+0.4", "−1.2" with a real minus sign; zero has no sign. */
export function signed(value: number, digits: number): string {
  const text = Math.abs(value).toFixed(digits)
  if (Number(text) === 0) return text
  return value > 0 ? `+${text}` : `−${text}`
}

/** An enum name as text: "LOW_FAT" → "Low fat". */
export function enumLabel(value: string): string {
  const words = value.toLowerCase().replaceAll('_', ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A typed number ("81,4" or "81.4"), or null when blank or not a finite number. */
export function parseNumber(value: string | number): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const text = value.trim().replace(',', '.')
  if (text === '') return null
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}
